import { useState, useEffect } from "react";
import { courseService } from "../../../services/courseService";
import api from "../../../services/api";

function DashboardDocuments() {
    const [activeModule, setActiveModule] = useState(null);
    const [modules, setModules] = useState([]);
    const [documents, setDocuments] = useState({}); 
    const [loading, setLoading] = useState(true);
    const [courseId, setCourseId] = useState(null);

    useEffect(() => {
        loadModules();
    }, []);

    useEffect(() => {
        if (activeModule && modules.length > 0) {
            loadModuleDocuments(activeModule);
        }
    }, [activeModule, modules]);

    const loadModules = async () => {
        setLoading(true);
        try {
            
            const coursesResult = await courseService.getCourses();
            if (coursesResult.success && coursesResult.data.courses.length > 0) {
                const firstCourse = coursesResult.data.courses[0];
                setCourseId(firstCourse.id);

                
                const modulesResult = await courseService.getCourseModules(firstCourse.id);
                if (modulesResult.success) {
                    const modulesData = modulesResult.data.modules || [];
                    const modulesWithDocuments = modulesData.filter(m => m.order !== 1);
                    setModules(modulesWithDocuments);
                    
                    if (modulesWithDocuments.length > 0) {
                        setActiveModule(modulesWithDocuments[0].id);
                    }
                }
            }
        } catch (error) {
            console.error('Failed to load modules:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadModuleDocuments = async (moduleId) => {
        const module = modules.find(m => m.id === moduleId);
        if (!module) return;

        try {
            const response = await api.get(`/documents/module/${moduleId}`);
            if (response.data.success && response.data.data.documents && response.data.data.documents.length > 0) {
                
                setDocuments(prev => ({
                    ...prev,
                    [moduleId]: response.data.data.documents
                }));
            } else {
                
                const s3Documents = getS3DocumentsForModule(module.order);
                setDocuments(prev => ({
                    ...prev,
                    [moduleId]: s3Documents
                }));
            }
        } catch (error) {
                
            const s3Documents = getS3DocumentsForModule(module.order);
            setDocuments(prev => ({
                ...prev,
                [moduleId]: s3Documents
            }));
        }
    };

    const getS3DocumentsForModule = (moduleOrder) => {
        const CLOUDFRONT_BASE = 'https://d3vx0tne0vxeo1.cloudfront.net/teachers/resources';
        
        const moduleDocuments = {
            1: [], 
            2: [
                { filename: 'M2 - Single Letter Sounds.png', title: 'Single Letter Sounds', type: 'image/png' },
                { filename: 'M2 - Short Vowel Sounds.png', title: 'Short Vowel Sounds', type: 'image/png' },
                { filename: 'M2 - Long Vowel Sounds.png', title: 'Long Vowel Sounds', type: 'image/png' },
                { filename: 'M2 See - Hear - Do.png', title: 'See - Hear - Do', type: 'image/png' },
            ],
            3: [
                { filename: 'M3 - Blending Basic Sounds.png', title: 'Blending Basic Sounds', type: 'image/png' },
                { filename: 'M3 - Non-Phonetic Words.png', title: 'Non-Phonetic Words', type: 'image/png' },
            ],
            4: [
                { filename: 'M4 - Consonant Blends.png', title: 'Consonant Blends', type: 'image/png' },
                { filename: 'M4 - Blending with Consonant Blends.png', title: 'Blending with Consonant Blends', type: 'image/png' },
            ],
            5: [
                { filename: 'M5 - Vowel Blends.png', title: 'Vowel Blends', type: 'image/png' },
                { filename: 'M5 - Blending with Vowel Blends.png', title: 'Blending with Vowel Blends', type: 'image/png' },
            ],
            6: [
                { filename: 'M6 - Vowel_Consonant Blends.png', title: 'Vowel/Consonant Blends', type: 'image/png' },
                { filename: 'M6 - Blending with Vowel_Consonant Blends.png', title: 'Blending with Vowel/Consonant Blends', type: 'image/png' },
            ],
            7: [
                { filename: 'M7 - Decoding Multi-Syllabic Words.pdf', title: 'Decoding Multi-Syllabic Words', type: 'application/pdf' },
            ],
            8: [
                { filename: 'M8 - Common Prefix.png', title: 'Common Prefix', type: 'image/png' },
                { filename: 'M8 - Blending with Prefix.png', title: 'Blending with Prefix', type: 'image/png' },
            ],
            9: [
                { filename: 'M9 - Common Suffix.png', title: 'Common Suffix', type: 'image/png' },
                { filename: 'M9 - Blending with Suffix.png', title: 'Blending with Suffix', type: 'image/png' },
            ],
            10: [
                { filename: 'M10 - Irregular Words.png', title: 'Irregular Words', type: 'image/png' },
            ],
        };

        const files = moduleDocuments[moduleOrder] || [];
        
        return files.map(file => ({
            id: `s3-${moduleOrder}-${file.filename}`,
            title: file.title,
            filename: file.filename,
            url: `${CLOUDFRONT_BASE}/${encodeURIComponent(file.filename)}`,
            mimeType: file.type,
            fileSize: null, 
        }));
    };

    const handleDownload = async (doc) => {
        try {
            let url;
            let filename = doc.filename || doc.title || 'download';
            
            if (doc.url) {
                
                const response = await api.get(`/documents/s3-download`, {
                    params: {
                        url: doc.url,
                        filename: doc.filename || filename
                    },
                    responseType: 'blob', 
                });
                
                const blobUrl = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                
                setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                }, 100);
            } else if (doc.id) {
                
                url = `/api/documents/${doc.id}/download`;
                const response = await fetch(url, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                
                if (!response.ok) throw new Error('Download failed');
                
                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                
                setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                }, 100);
            }
        } catch (error) {
            alert(`Failed to download file: ${error.message}. Please try again.`);
        }
    };

    if (loading) {
        return (
            <div className="bg-[#E2F2FF] rounded-xl p-6 shadow-md border border-blue-200">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-center py-16">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="module-tabs">
                {modules.map((module) => (
                    <button
                        key={module.id}
                        className={`module-tab ${
                            activeModule === module.id
                                ? "active"
                                : ""
                        }`}
                        onClick={() => setActiveModule(module.id)}
                    >
                        Module {module.order}
                    </button>
                ))}
            </div>

            <div className="bg-[#E2F2FF] rounded-xl p-6 shadow-md border border-blue-200">
                <div className="max-w-7xl mx-auto">
                    <div className="download-list">
                        {activeModule && documents[activeModule] && documents[activeModule].length > 0 ? (
                            <div className="module-content lg:pl-16 lg:pr-16">
                                {documents[activeModule].map((doc) => (
                                    <DownloadItem
                                        key={doc.id}
                                        document={doc}
                                        onDownload={handleDownload}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="module-content lg:pl-16 lg:pr-16 text-center py-8 text-gray-500">
                                <p>No documents available for this module.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

// 🔽 Reusable Download Item Component
function DownloadItem({ document: doc, onDownload }) {
    const getFileIcon = (mimeType) => {
        if (mimeType?.includes('pdf')) {
            return (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                </svg>
            );
        } else if (mimeType?.includes('image')) {
            return (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
            );
        }
        return (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
        );
    };

    return (
        <div className="download-item">
            <div className="download-info">
                <div className="file-icon">
                    {getFileIcon(doc.mimeType)}
                </div>
                <div className="file-name">{doc.title || doc.filename}</div>
            </div>

            <button 
                className="download-btn"
                onClick={() => onDownload(doc)}
            >
                <span>Download</span>
                <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                </svg>
            </button>
        </div>
    );
}

export default DashboardDocuments;
